import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import * as DocumentPicker from 'expo-document-picker';
import { FlowchartDesigner, FlowchartViewer, buildLinearFlowchart, type FlowchartData, type FlowchartNode, type FlowchartEdge } from '../../components/Flowchart';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, KPIDeleteConfirmModal } from '../../components';
import { useResponsive, useRoleNavigation, useAutoTranslate, useUnsavedChangesWarning } from '../../hooks';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { extractKpiErrorMessage, validateKpiRequiredFields } from '../../utils/kpiErrors';
import { normalizeArabicNumbers } from '../../utils/rtl';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { subProcessesApi, processesApi, goalsApi, organizationalUnitsApi, kpisApi, processStepsApi, employeesApi } from '../../services/apiService';
import { StrategicGoalList, OrganizationalUnitList, ProcessList, KPIType, Polarity, Frequency, SubProcess, Employee } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;
type RouteProps = RouteProp<MainStackParamList, 'AddSubProcess'>;

// ============================================================
// SVG Icons
// ============================================================
const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MenuIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 12H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M4 6H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M4 18H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const InfoIcon = ({ size = 20, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 16V12" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 8H12.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Portal Dropdown Component (uses raw HTML divs for proper scroll handling)
// ============================================================
interface PortalDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  buttonRef: React.RefObject<View>;
  children: React.ReactNode;
  backgroundColor?: string;
  borderColor?: string;
}

const PortalDropdown = ({ isOpen, onClose, buttonRef, children, backgroundColor = '#FFFFFF', borderColor = '#E2E8F0' }: PortalDropdownProps) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false, availableHeight: 300 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      (buttonRef.current as any).measureInWindow((x: number, y: number, width: number, height: number) => {
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - (y + height + 4);
        const spaceAbove = y - 4;
        const maxDropdown = 300;
        const openUpward = spaceBelow < 150 && spaceAbove > spaceBelow;
        setPosition({
          top: openUpward ? Math.max(4, y - Math.min(maxDropdown, spaceAbove)) : y + height + 4,
          left: x,
          width: width,
          openUpward,
          availableHeight: openUpward ? Math.min(maxDropdown, spaceAbove) : Math.min(maxDropdown, spaceBelow),
        });
      });
    }
  }, [isOpen, buttonRef]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const dropdownMenu = (
    <>
      {/* eslint-disable-next-line */}
      <div
        onMouseDown={(e: any) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
        style={{
          position: 'fixed' as const,
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 9998,
        }}
      />
      <div
        ref={dropdownRef}
        onMouseDown={(e: any) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: position.width,
          backgroundColor: backgroundColor,
          border: `1px solid ${borderColor}`,
          borderRadius: 8,
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
          zIndex: 9999,
          maxHeight: position.availableHeight,
          overflowY: 'auto' as const,
        }}
      >
        {children}
      </div>
    </>
  );

  return ReactDOM.createPortal(dropdownMenu, document.body);
};

// Step Form Data Interface
interface StepFormData {
  id: string;
  order: string;
  // Flowchart link: type drives the node shape (خطوة rectangle / قرار diamond);
  // nodeId is the stable id tying this row to its flowchart node both ways.
  type: 'step' | 'decision';
  nodeId: string;
  name: string;
  description: string;
  responsible: string;
  organizational_units: number[];  // ManyToMany - multiple units
  responsible_employees: number[]; // ManyToMany - multiple employees
  duration: string;
  timeUnit: string;
  tools: string;
  isExpanded: boolean;
  documentFile?: DocumentPicker.DocumentPickerAsset | null;
}

// KPI Form Data Interface
interface KPIFormData {
  id: string;
  name: string;
  description: string;
  weight: string;
  kpiType: string;
  polarity: string;
  unit: string;
  targetValue: string;
  worstAcceptableValue: string;
  frequency: string;
  formula: string;
  dataSource: string;
  measurementStartDate: string;
  isExpanded: boolean;
}

const createEmptyStep = (order: number): StepFormData => {
  const id = Math.random().toString(36).substr(2, 9);
  return {
  id,
  order: order.toString(),
  type: 'step',
  nodeId: `step_${id}`,
  name: '',
  description: '',
  responsible: '',
  organizational_units: [],  // ManyToMany - empty array
  responsible_employees: [], // ManyToMany - empty array
  duration: '',
  timeUnit: '',
  tools: '',
  isExpanded: true,
  };
};

// ── Flowchart ↔ steps sync helpers ────────────────────────────────────────
// إجراءات العملية (steps) and the flowchart are kept in sync two-way: each
// step row maps to a خطوة/قرار node (label = step name); بداية/نهاية are
// auto-managed nodes with no step row. New procedures auto-connect in order.
const FC_NODE_SPACING_X = 220;
const FC_ROW_Y = 100;
const FC_START_ID = 'auto_start';
const FC_END_ID = 'auto_end';

// steps → flowchart: preserve existing node positions; only re-chain the
// linear edges when the node SET changes (add/remove), so a rename/type change
// or manual edge edits aren't clobbered.
function buildFlowFromSteps(
  steps: StepFormData[],
  prev: FlowchartData | null | undefined,
  labels: { start: string; end: string },
): FlowchartData {
  const prevNodes = prev?.nodes || [];
  const posById = new Map(prevNodes.map((n) => [n.id, n.position]));
  const total = steps.length + 2;
  const rightmostX = (total - 1) * FC_NODE_SPACING_X;

  const startNode: FlowchartNode = {
    id: FC_START_ID, type: 'start', label: labels.start,
    position: posById.get(FC_START_ID) || { x: rightmostX, y: FC_ROW_Y },
  };
  const stepNodes: FlowchartNode[] = steps.map((s, i) => ({
    id: s.nodeId,
    type: s.type,
    label: s.name || '',
    position: posById.get(s.nodeId) || { x: rightmostX - (i + 1) * FC_NODE_SPACING_X, y: FC_ROW_Y },
  }));
  const endNode: FlowchartNode = {
    id: FC_END_ID, type: 'end', label: labels.end,
    position: posById.get(FC_END_ID) || { x: 0, y: FC_ROW_Y },
  };
  const nodes = [startNode, ...stepNodes, endNode];

  const prevStepIds = prevNodes
    .filter((n) => n.type !== 'start' && n.type !== 'end')
    .map((n) => n.id);
  const curStepIds = stepNodes.map((n) => n.id);
  const sameSet = prevStepIds.length === curStepIds.length
    && prevStepIds.every((id, i) => id === curStepIds[i]);

  let edges: FlowchartEdge[];
  if (sameSet && prev && prev.edges && prev.edges.length > 0) {
    edges = prev.edges;  // keep manual/existing edges on rename/type change
  } else {
    edges = [];
    for (let i = 0; i < nodes.length - 1; i += 1) {
      edges.push({
        id: `auto_e_${nodes[i].id}__${nodes[i + 1].id}`,
        source: nodes[i].id,
        target: nodes[i + 1].id,
        sourceHandle: 'right',
        targetHandle: 'left',
      });
    }
  }
  // Never emit an edge pointing at a node we didn't keep — the kept-edges
  // branch above reuses the previous edge list, which can outlive a node that
  // was dropped from the step set. The backend rejects dangling edges with
  // "الوصلة … تشير إلى عقدة غير موجودة".
  const nodeIds = new Set(nodes.map((n) => n.id));
  edges = edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));
  return { version: '1.0', nodes, edges };
}

// flowchart → steps: mirror canvas node add/remove/relabel/type back into the
// steps list (start/end nodes are ignored — they have no procedure row).
function reconcileStepsFromFlow(
  flow: FlowchartData | null | undefined,
  steps: StepFormData[],
): StepFormData[] {
  const stepNodes = (flow?.nodes || []).filter(
    (n) => n.type === 'step' || n.type === 'decision',
  );
  const byNodeId = new Map(steps.map((s) => [s.nodeId, s]));
  return stepNodes.map((node, i) => {
    const existing = byNodeId.get(node.id);
    const type = node.type === 'decision' ? 'decision' : 'step';
    if (existing) {
      return {
        ...existing,
        type,
        name: node.label != null ? node.label : existing.name,
        order: (i + 1).toString(),
      };
    }
    // New node drawn on the canvas → fresh step row for the user to fill in.
    const base = createEmptyStep(i + 1);
    return { ...base, nodeId: node.id, type, name: node.label || '' };
  });
}

const createEmptyKPI = (): KPIFormData => ({
  id: Math.random().toString(36).substr(2, 9),
  name: '',
  description: '',
  weight: '',
  kpiType: '',
  polarity: '',
  unit: '',
  targetValue: '',
  worstAcceptableValue: '',
  frequency: '',
  formula: '',
  dataSource: '',
  measurementStartDate: '',
  isExpanded: true,
});

export default function AddSubProcessScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const processId = route.params?.processId;
  const subProcessId = route.params?.subProcessId;
  const scrollToKpi = route.params?.scrollToKpi;
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const isEditMode = !!subProcessId;
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate, translateUnit } = useAutoTranslate();

  // Theme colors for dark mode support
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F1F5F9',
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    dropdown: colors.card,
    dropdownBorder: colors.border,
    dropdownText: isDark ? '#6B7280' : '#9CA3AF',
    dropdownTextSelected: colors.textPrimary,
    dropdownMenu: colors.card,
    dropdownMenuBorder: colors.border,
    dropdownItem: colors.textPrimary,
    dropdownItemBorder: isDark ? colors.border : '#F3F4F6',
    stepCard: colors.card,
    stepCardBorder: colors.cardBorder,
    stepHeader: colors.card,
    stepHeaderTitle: colors.textPrimary,
    stepNumberBadge: isDark ? colors.backgroundAlt : '#E2E8F0',
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiHeader: colors.card,
    kpiHeaderTitle: colors.textPrimary,
    kpiNumberBadge: isDark ? colors.backgroundAlt : '#E2E8F0',
    kpiSubsection: isDark ? colors.backgroundAlt : '#F1F5F9',
    weightInputWrapper: colors.card,
    weightButton: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
    weightButtonBorder: isDark ? '#4B5563' : '#CBD5E1',
    addButton: colors.card,
    addButtonBorder: isDark ? '#4B5563' : '#CBD5E1',
    addButtonText: colors.textPrimary,
    cancelButton: colors.card,
    cancelButtonBorder: colors.border,
    cancelButtonText: colors.textMuted,
    loadingText: colors.textMuted,
    dateInput: colors.card,
    dateInputBorder: colors.border,
  };

  // Redirect logic lives after `user` is resolved (see canCreateOperationalItem
  // below) so Section Heads who hold executive_items.create aren't bounced.

  // Refs for scrolling
  const scrollViewRef = useRef<ScrollView>(null);
  const kpiSectionRef = useRef<View>(null);
  const [kpiSectionY, setKpiSectionY] = useState<number>(0);

  const { width } = useWindowDimensions();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();

  // Proposing/editing a sub-process is governed by `hrcpms.executive_items.create`
  // (held by Section Heads), NOT the coarse isViewOnly flag. A Strategy Section
  // Head also has strategic_goals.create → isViewOnly, so without this escape
  // hatch they'd be bounced the instant they open this screen.
  const canCreateOperationalItem = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.executive_items.create')
  );

  // Redirect genuinely view-only users (DG / Director / pure Strategy Team).
  useEffect(() => {
    if (isViewOnly && !canCreateOperationalItem) {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('Processes' as any);
      }
    }
  }, [isViewOnly, canCreateOperationalItem, navigation]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);
  const [units, setUnits] = useState<OrganizationalUnitList[]>([]);
  const [processes, setProcesses] = useState<ProcessList[]>([]);

  // Track if fields are inherited from parent process (should be readonly)
  const [isInheritedFromParent, setIsInheritedFromParent] = useState(false);
  const [parentProcessName, setParentProcessName] = useState<string>('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    // Two-part name: the stored `name` is built as `${subName} - ${opName}`.
    subName: '',   // العملية الفرعية
    opName: '',    // اسم العملية
    description: '',
    code: '',
    strategicGoal: null as number | null,
    parentProcess: processId || null as number | null,
    organizationalUnit: null as number | null,
    // تفاصيل العملية fields
    employeesCount: '',
    weight: '',
    processTime: '',
    timeUnit: '',
    executionFrequency: '',
    processCost: '',
    approvalPerson: '',
    processFlowImages: '',
    // Interactive flowchart payload (directorate request 2026-05-13).
    // Sits alongside the legacy process_flow_diagram FileField above —
    // sub-processes can keep their uploaded image OR draw a new one.
    flowchartData: null as import('../../components/Flowchart').FlowchartData | null,
  });

  useUnsavedChangesWarning(!!formData.subName || !!formData.opName || !!formData.description);

  const [steps, setSteps] = useState<StepFormData[]>([createEmptyStep(1)]);
  // Which side last edited, so the bidirectional steps↔flowchart sync runs in
  // exactly one direction per change and never loops. Cleared by the effect.
  const syncSourceRef = useRef<'steps' | 'flow' | null>(null);
  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [kpiPendingDelete, setKpiPendingDelete] = useState<{ id: string; backendId: number | null; name: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedFile, setSelectedFile] = useState<DocumentPicker.DocumentPickerAsset | null>(null);
  const [existingFileUrl, setExistingFileUrl] = useState<string | null>(null);

  // Employees per step (keyed by step.id)
  const [stepEmployees, setStepEmployees] = useState<Record<string, Employee[]>>({});
  const [loadingEmployees, setLoadingEmployees] = useState<Record<string, boolean>>({});

  // Step dropdowns state (keyed by step.id)
  const [activeStepUnitDropdown, setActiveStepUnitDropdown] = useState<string | null>(null);
  const [activeStepEmployeeDropdown, setActiveStepEmployeeDropdown] = useState<string | null>(null);
  const [activeStepTimeUnitDropdown, setActiveStepTimeUnitDropdown] = useState<string | null>(null);
  const [stepUnitSearch, setStepUnitSearch] = useState<Record<string, string>>({});
  const [stepEmployeeSearch, setStepEmployeeSearch] = useState<Record<string, string>>({});

  // Dropdowns
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);
  const [showProcessDropdown, setShowProcessDropdown] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [showClassificationDropdown, setShowClassificationDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showTimeUnitDropdown, setShowTimeUnitDropdown] = useState(false);
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);
  const [activeKpiDropdown, setActiveKpiDropdown] = useState<{ kpiId: string; type: string } | null>(null);

  // Dropdown trigger refs for portal positioning
  const goalDropdownRef = useRef<View>(null);
  const processDropdownRef = useRef<View>(null);
  const unitDropdownRef = useRef<View>(null);
  const timeUnitDropdownRef = useRef<View>(null);
  const frequencyDropdownRef = useRef<View>(null);


  // Dropdown position state (for non-PortalDropdown dropdowns)
  const [timeUnitDropdownPos, setTimeUnitDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const isSmallScreen = width < 768;

  // Dropdown options
  const classificationOptions = [
    { value: 'main', label: t('common.main') },
    { value: 'sub', label: t('subProcesses.sub') },
  ];

  const statusOptions = [
    { value: 'active', label: t('common.active') },
    { value: 'inactive', label: t('common.inactive') },
    { value: 'draft', label: t('common.draft') },
  ];

  // KPI type options
  const kpiTypeOptions = [
    // 'strategic' omitted — it requires data_source_type (مستقل/تجميعي, DTS
    // 4.3), collected only on AddGoalScreen. Offering it on an operational
    // screen 400s the KPI create and silently drops it on save.
    { value: 'operational', label: t('kpis.operational') },
    { value: 'efficiency', label: t('kpis.efficiency') },
    { value: 'effectiveness', label: t('kpis.effectiveness') },
    { value: 'impact', label: t('kpis.impact') },
  ];

  const polarityOptions = [
    { value: 'positive', label: t('kpis.ascending') },
    { value: 'negative', label: t('kpis.descending') },
  ];

  const unitOptions = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'number', label: t('kpis.number') },
    { value: 'currency', label: t('kpis.currency') },
  ];

  const frequencyOptions = [
    { value: 'daily', label: t('kpis.daily') },
    { value: 'weekly', label: t('kpis.weekly') },
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];

  // Time unit options
  const timeUnitOptions = [
    { value: 'minute', label: t('kpis.minute') },
    { value: 'hour', label: t('kpis.hour') },
    { value: 'day', label: t('kpis.day') },
    { value: 'week', label: t('kpis.week') },
    { value: 'month', label: t('kpis.month') },
    { value: 'year', label: t('kpis.year') },
  ];

  // Execution frequency options (دورية التنفيذ)
  const executionFrequencyOptions = [
    { value: 'daily', label: t('kpis.daily') || 'يومي' },
    { value: 'weekly', label: t('kpis.weekly') || 'أسبوعي' },
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];

  // File picker handler for step document template
  const handleStepFilePick = async (stepId: string) => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/*', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        copyToCacheDirectory: true,
      });
      if (!result.canceled && result.assets && result.assets.length > 0) {
        setSteps(steps.map(s => s.id === stepId ? { ...s, documentFile: result.assets[0] } : s));
      }
    } catch (error) {
      console.error('Error picking step file:', error);
      showError(t('readings.filePickFailed'));
    }
  };

  // File picker handler
  const handleFilePick = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/*', 'application/pdf'],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        setSelectedFile(result.assets[0]);
      }
    } catch (error) {
      console.error('Error picking file:', error);
      showError(t('readings.filePickFailed'));
    }
  };

  const toggleKpiDropdown = (kpiId: string, dropdownType: string) => {
    if (activeKpiDropdown?.kpiId === kpiId && activeKpiDropdown?.type === dropdownType) {
      setActiveKpiDropdown(null);
    } else {
      setActiveKpiDropdown({ kpiId, type: dropdownType });
    }
    closeAllDropdowns();
  };

  const closeAllDropdowns = () => {
    setShowGoalDropdown(false);
    setShowProcessDropdown(false);
    setShowUnitDropdown(false);
    setShowClassificationDropdown(false);
    setShowStatusDropdown(false);
    setShowTimeUnitDropdown(false);
    setShowFrequencyDropdown(false);
    // Also close step dropdowns
    setActiveStepUnitDropdown(null);
    setActiveStepEmployeeDropdown(null);
    setActiveStepTimeUnitDropdown(null);
  };

  // Helper function to measure dropdown button position
  // Handle dropdown open
  const handleOpenGoalDropdown = () => {
    setShowGoalDropdown(!showGoalDropdown);
    setShowProcessDropdown(false);
    setShowUnitDropdown(false);
    setActiveKpiDropdown(null);
  };

  const handleOpenProcessDropdown = () => {
    setShowProcessDropdown(!showProcessDropdown);
    setShowGoalDropdown(false);
    setShowUnitDropdown(false);
    setActiveKpiDropdown(null);
  };

  const handleOpenUnitDropdown = () => {
    setShowUnitDropdown(!showUnitDropdown);
    setShowGoalDropdown(false);
    setShowProcessDropdown(false);
    setActiveKpiDropdown(null);
  };

  const handleOpenTimeUnitDropdown = () => {
    setShowTimeUnitDropdown(!showTimeUnitDropdown);
    setShowGoalDropdown(false);
    setShowProcessDropdown(false);
    setShowUnitDropdown(false);
    setActiveKpiDropdown(null);
  };


  useEffect(() => {
    fetchData();
  }, [subProcessId]);

  // Handle scrollToKpi - auto-add a KPI form and scroll to the section
  useEffect(() => {
    if (scrollToKpi && !loading) {
      // Add a new KPI form if there are no KPIs yet
      setTimeout(() => {
        if (kpis.length === 0) {
          setKpis([createEmptyKPI()]);
        }
        // Scroll to the KPI section
        if (scrollViewRef.current && kpiSectionY > 0) {
          scrollViewRef.current.scrollTo({ y: kpiSectionY - 100, animated: true });
        }
      }, 300);
    }
  }, [scrollToKpi, loading, kpiSectionY]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [goalsData, unitsData, processesData] = await Promise.all([
        goalsApi.list({ page_size: 100 }),
        organizationalUnitsApi.list({ page_size: 100 }),
        processesApi.list({ page_size: 100 }),
      ]);
      setGoals(goalsData.results || []);
      setUnits(unitsData.results || []);
      setProcesses(processesData.results || []);

      // If editing, fetch the sub-process data
      if (subProcessId) {
        const subProcessData = await subProcessesApi.get(subProcessId);
        // Split the stored "العملية الفرعية - اسم العملية" back into its parts.
        const _fullName = subProcessData.name || '';
        const _sep = _fullName.indexOf(' - ');
        setFormData({
          name: _fullName,
          subName: _sep >= 0 ? _fullName.slice(0, _sep) : _fullName,
          opName: _sep >= 0 ? _fullName.slice(_sep + 3) : '',
          description: subProcessData.description || '',
          code: subProcessData.code || '',
          strategicGoal: subProcessData.strategic_goal || null,
          parentProcess: subProcessData.process || null,
          organizationalUnit: subProcessData.organizational_unit || null,
          weight: subProcessData.weight?.toString() || '',
          employeesCount: subProcessData.employees_count?.toString() || '',
          processTime: subProcessData.process_time?.toString() || '',
          timeUnit: subProcessData.time_unit || '',
          executionFrequency: subProcessData.execution_frequency || '',
          processCost: subProcessData.cost?.toString() || '',
          approvalPerson: subProcessData.approval_person || '',
          processFlowImages: '',
          flowchartData: (subProcessData as any).flowchart_data || null,
        });

        // Set existing file URL if available
        if (subProcessData.process_flow_diagram) {
          setExistingFileUrl(subProcessData.process_flow_diagram);
        }

        // Load existing steps from subProcessData.steps
        if (subProcessData.steps && subProcessData.steps.length > 0) {
          const loadedSteps: StepFormData[] = subProcessData.steps.map((step: any) => ({
            id: step.id.toString(),
            order: step.order || 1,
            type: (step.step_type === 'decision' ? 'decision' : 'step') as 'step' | 'decision',
            // Keep the saved node id so the row re-links to its flowchart node
            // on reload; fall back to a deterministic id for legacy rows.
            nodeId: step.flowchart_node_id || `step_${step.id}`,
            name: step.name || '',
            description: step.description || '',
            responsible: step.responsible || '',
            // ManyToMany fields - ensure arrays
            organizational_units: step.organizational_units || [],
            responsible_employees: step.responsible_employees || [],
            duration: step.duration || '',
            timeUnit: step.time_unit || '',
            tools: step.tools || '',
            isExpanded: false,
          }));
          setSteps(loadedSteps);

          // Fetch employees for each step that has organizational units
          loadedSteps.forEach((step) => {
            if (step.organizational_units && step.organizational_units.length > 0) {
              fetchEmployeesForStep(step.id, step.organizational_units);
            }
          });
        }

        // Fetch existing KPIs for this sub-process
        const existingKpis = await kpisApi.list({ source_sub_process: subProcessId, page_size: 100 });
        if (existingKpis.results && existingKpis.results.length > 0) {
          const loadedKpis: KPIFormData[] = existingKpis.results.map((kpi: any) => ({
            id: kpi.id.toString(),
            name: kpi.name || '',
            description: kpi.description || '',
            weight: kpi.weight?.toString() || '',
            kpiType: kpi.kpi_type || '',
            polarity: kpi.polarity || '',
            unit: kpi.unit || '',
            targetValue: kpi.target_value?.toString() || '',
            worstAcceptableValue: kpi.worst_acceptable_value != null ? String(kpi.worst_acceptable_value) : '',
            frequency: kpi.frequency || '',
            formula: kpi.measurement_formula || '',
            dataSource: kpi.data_source || '',
            measurementStartDate: kpi.measurement_start_date || '',
            isExpanded: false,
          }));
          setKpis(loadedKpis);
        }
      }
      // If adding new subprocess with parent process ID, fetch parent process data and auto-fill
      else if (processId && !subProcessId) {
        try {
          const parentProcess = await processesApi.get(processId);
          setIsInheritedFromParent(true);
          setParentProcessName(parentProcess.name || '');

          // Inherit structural fields from parent process
          // Name and description left empty for user to fill
          setFormData(prev => ({
            ...prev,
            parentProcess: parentProcess.id,                   // Inherited, disabled
            strategicGoal: parentProcess.strategic_goal || null, // Inherited, disabled
            organizationalUnit: parentProcess.organizational_units?.[0] || null, // Inherited from first parent unit
          }));
        } catch (err) {
          console.error('Failed to fetch parent process:', err);
          // Still set the processId even if fetch fails
          setFormData(prev => ({
            ...prev,
            parentProcess: processId,
          }));
        }
      }
    } catch (err) {
      showError(t('processes.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.subName.trim()) {
      newErrors.subName = t('operations.subProcessRequired');
    }
    if (!formData.opName.trim()) {
      newErrors.opName = t('operations.operationNameRequired');
    }
    if (!formData.parentProcess) {
      newErrors.parentProcess = t('validation.mainProcessRequired');
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    const kpiError = validateKpiRequiredFields(kpis, t, 'measurementStartDate');
    if (kpiError) { showError(kpiError); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequiredFields'));
      return;
    }

    setSubmitting(true);
    try {
      // Prepare data - use FormData if file is selected, otherwise use regular object
      let subProcessData: Partial<SubProcess> | FormData;
      let savedSubProcessId: number;

      if (selectedFile) {
        // Use FormData for file upload
        const formDataPayload = new FormData();
        formDataPayload.append('process', String(formData.parentProcess));
        formDataPayload.append('name', [formData.subName.trim(), formData.opName.trim()].filter(Boolean).join(' - '));
        formDataPayload.append('description', formData.description || '');
        formDataPayload.append('code', formData.code || '');
        if (formData.strategicGoal) formDataPayload.append('strategic_goal', String(formData.strategicGoal));
        if (formData.organizationalUnit) formDataPayload.append('organizational_unit', String(formData.organizationalUnit));
        formDataPayload.append('weight', formData.weight || '0');
        if (formData.processTime) formDataPayload.append('process_time', formData.processTime);
        if (formData.timeUnit) formDataPayload.append('time_unit', formData.timeUnit);
        if (formData.employeesCount) formDataPayload.append('employees_count', formData.employeesCount);
        if (formData.executionFrequency) formDataPayload.append('execution_frequency', formData.executionFrequency);
        if (formData.processCost) formDataPayload.append('cost', formData.processCost);
        if (formData.approvalPerson) formDataPayload.append('approval_person', formData.approvalPerson);
        // FormData carries JSON as a string; backend's JSONField
        // serializer will parse it transparently.
        if (formData.flowchartData) {
          formDataPayload.append('flowchart_data', JSON.stringify(formData.flowchartData));
        }

        // Add file
        if (Platform.OS === 'web') {
          const response = await fetch(selectedFile.uri);
          const blob = await response.blob();
          const file = new File([blob], selectedFile.name || 'diagram.pdf', {
            type: selectedFile.mimeType || 'application/pdf',
          });
          console.log('📎 File upload (web):', {
            name: file.name,
            type: file.type,
            size: file.size,
          });
          formDataPayload.append('process_flow_diagram', file);
        } else {
          const fileData = {
            uri: selectedFile.uri,
            type: selectedFile.mimeType || 'application/octet-stream',
            name: selectedFile.name || 'diagram.pdf',
          };
          console.log('📎 File upload (native):', fileData);
          formDataPayload.append('process_flow_diagram', fileData as any);
        }

        console.log('📤 Sending FormData with file attachment');
        subProcessData = formDataPayload;
      } else {
        // Use regular object for no file
        subProcessData = {
          process: formData.parentProcess!,
          name: [formData.subName.trim(), formData.opName.trim()].filter(Boolean).join(' - '),
          description: formData.description || '',
          code: formData.code || '',
          strategic_goal: formData.strategicGoal || null,
          organizational_unit: formData.organizationalUnit || null,
          weight: formData.weight || '0',
          process_time: formData.processTime ? parseInt(formData.processTime, 10) : null,
          time_unit: formData.timeUnit || '',
          employees_count: formData.employeesCount ? parseInt(formData.employeesCount, 10) : null,
          execution_frequency: formData.executionFrequency || '',
          cost: formData.processCost ? parseFloat(formData.processCost) : null,
          approval_person: formData.approvalPerson || '',
          flowchart_data: formData.flowchartData ?? null,
        } as any;
      }

      if (isEditMode && subProcessId) {
        await subProcessesApi.update(subProcessId, subProcessData);
        savedSubProcessId = subProcessId;
      } else {
        const createdSubProcess = await subProcessesApi.create(subProcessData);
        savedSubProcessId = createdSubProcess.id;
      }

      // Save steps (خطوات العملية)
      if (steps.length > 0) {
        const stepPromises = steps
          .filter(step => step.name.trim())
          .map(async (step, index) => {
            const stepData = {
              sub_process: savedSubProcessId,
              order: index + 1,
              step_type: step.type || 'step',
              flowchart_node_id: step.nodeId || '',
              name: step.name,
              description: step.description || '',
              // ManyToMany fields - send arrays of IDs
              organizational_units: step.organizational_units || [],
              responsible_employees: step.responsible_employees || [],
              duration: step.duration || '',
              time_unit: step.timeUnit || '',
              tools: step.tools || '',
            };

            // Check if this is an existing step (numeric ID) or new one (random string ID)
            const numericId = parseInt(step.id, 10);
            if (!isNaN(numericId) && numericId > 0) {
              // Try to update existing step, fall back to create if not found
              try {
                return await processStepsApi.update(numericId, stepData);
              } catch (err: any) {
                if (err.response?.status === 404) {
                  // Step doesn't exist, create new one
                  return processStepsApi.create(stepData);
                }
                throw err;
              }
            } else {
              // Create new step
              return processStepsApi.create(stepData);
            }
          });

        await Promise.all(stepPromises);
      }

      // Handle KPIs - create new ones and update existing ones
      const kpiErrors: string[] = [];
      const validKpis = kpis.filter(kpi => kpi.name.trim());
      for (const kpi of validKpis) {
        const kpiData: any = {
          name: kpi.name,
          description: kpi.description || '',
          source_sub_process: savedSubProcessId,
          kpi_type: (kpi.kpiType || 'operational') as KPIType,
          polarity: (kpi.polarity || 'positive') as Polarity,
          unit: kpi.unit || 'number',
          target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
          worst_acceptable_value:
            kpi.polarity === 'negative' && kpi.unit !== '%' && kpi.worstAcceptableValue !== ''
              ? (parseFloat(kpi.worstAcceptableValue) || 0).toFixed(2)
              : null,
          frequency: (kpi.frequency || 'monthly') as Frequency,
          measurement_formula: kpi.formula || '',
          data_source: kpi.dataSource || '',
          weight: kpi.weight || '0',
          source_type: 'sub_process',
        };

        if (kpi.measurementStartDate && kpi.measurementStartDate.trim()) {
          kpiData.measurement_start_date = kpi.measurementStartDate;
        } else {
          kpiData.measurement_start_date = null;
        }

        const numericId = parseInt(kpi.id, 10);
        if (!isNaN(numericId) && numericId > 0) {
          try {
            await kpisApi.update(numericId, kpiData);
          } catch (err: any) {
            if (err.response?.status === 404) {
              try {
                await kpisApi.create(kpiData);
              } catch (createError: any) {
                const errMsg = extractKpiErrorMessage(createError?.response?.data);
                kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
              }
            } else {
              const errMsg = extractKpiErrorMessage(err?.response?.data);
              kpiErrors.push(t('kpis.kpiUpdateError', { name: kpi.name, error: errMsg }));
            }
          }
        } else {
          try {
            await kpisApi.create(kpiData);
          } catch (createError: any) {
            const errMsg = extractKpiErrorMessage(createError?.response?.data);
            kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
          }
        }
      }

      if (kpiErrors.length > 0) {
        showError(kpiErrors.join('\n'));
      }

      showSuccess(isEditMode ? t('subProcesses.updateSuccess') : t('subProcesses.createSuccess'));
      queryClient.invalidateQueries({ queryKey: queryKeys.processes.all });
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('Processes' as any);
      }
    } catch (err: any) {
      console.error('Error saving sub-process:', err);
      console.error('Error response data:', err.response?.data);
      console.error('Error response status:', err.response?.status);
      let errorMessage = isEditMode ? t('subProcesses.updateFailed') : t('subProcesses.createFailed');
      if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (typeof data === 'object') {
          // Extract field-specific errors
          const fieldErrors = Object.entries(data)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
            console.error('Field errors:', fieldErrors);
          }
        }
      }
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('Processes' as any);
    }
  };

  // Step handlers
  const handleAddStep = () => {
    syncSourceRef.current = 'steps';  // mirror the new procedure onto the flowchart
    setSteps([...steps, createEmptyStep(steps.length + 1)]);
  };

  const handleRemoveStep = async (stepId: string) => {
    if (steps.length > 1) {
      // Check if this is an existing step (numeric ID)
      const numericId = parseInt(stepId, 10);
      if (!isNaN(numericId) && numericId > 0) {
        try {
          await processStepsApi.delete(numericId);
          showSuccess(t('subProcesses.stepDeleteSuccess'));
        } catch (err) {
          showError(t('subProcesses.stepDeleteFailed'));
          return;
        }
      }
      syncSourceRef.current = 'steps';  // remove its node from the flowchart too
      setSteps(steps.filter(s => s.id !== stepId));
    }
  };

  const handleStepChange = (stepId: string, field: keyof StepFormData, value: string) => {
    // name/type edits flow to the matching node label/shape; other fields are
    // a no-op for the flowchart (the sync rebuild is value-equal → skipped).
    syncSourceRef.current = 'steps';
    setSteps(steps.map(s => s.id === stepId ? { ...s, [field]: value } : s));
  };

  const toggleStepExpand = (stepId: string) => {
    // pure UI state — must NOT trigger a flowchart resync
    setSteps(steps.map(s => s.id === stepId ? { ...s, isExpanded: !s.isExpanded } : s));
  };

  // ── Bidirectional steps ↔ flowchart sync ──────────────────────────────
  // Runs in exactly one direction based on which side was last edited; the
  // ref is cleared first so the resulting state update doesn't bounce back.
  useEffect(() => {
    const src = syncSourceRef.current;
    if (src === 'steps') {
      syncSourceRef.current = null;
      const nextFlow = buildFlowFromSteps(steps, formData.flowchartData, {
        start: t('flowchart.nodeTypes.start'),
        end: t('flowchart.nodeTypes.end'),
      });
      if (JSON.stringify(nextFlow) !== JSON.stringify(formData.flowchartData)) {
        setFormData((prev) => ({ ...prev, flowchartData: nextFlow }));
      }
    } else if (src === 'flow') {
      syncSourceRef.current = null;
      const nextSteps = reconcileStepsFromFlow(formData.flowchartData, steps);
      if (JSON.stringify(nextSteps) !== JSON.stringify(steps)) {
        setSteps(nextSteps);
      }
    }
  }, [steps, formData.flowchartData, t]);

  // Fetch employees for multiple organizational units
  const fetchEmployeesForStep = async (stepId: string, unitIds: number[]) => {
    if (!unitIds || unitIds.length === 0) {
      setStepEmployees(prev => ({ ...prev, [stepId]: [] }));
      return;
    }

    setLoadingEmployees(prev => ({ ...prev, [stepId]: true }));

    try {
      // Use comma-separated IDs for multiple units filter
      const response = await employeesApi.list({ organizational_units: unitIds.join(',') });
      setStepEmployees(prev => ({
        ...prev,
        [stepId]: Array.isArray(response) ? response : (response.results || [])
      }));
    } catch (error) {
      console.error('Error fetching employees:', error);
      setStepEmployees(prev => ({ ...prev, [stepId]: [] }));
    } finally {
      setLoadingEmployees(prev => ({ ...prev, [stepId]: false }));
    }
  };

  // Toggle organizational unit selection (multi-select)
  const handleStepUnitToggle = (stepId: string, unitId: number) => {
    setSteps(steps.map(s => {
      if (s.id !== stepId) return s;

      const currentUnits = s.organizational_units || [];
      let newUnits: number[];

      if (currentUnits.includes(unitId)) {
        // Remove unit
        newUnits = currentUnits.filter(id => id !== unitId);
      } else {
        // Add unit
        newUnits = [...currentUnits, unitId];
      }

      // Remove employees that are no longer in selected units
      const newEmployees = s.responsible_employees.filter(empId => {
        const emp = stepEmployees[stepId]?.find(e => e.id === empId);
        return emp && newUnits.includes(emp.organizational_unit || 0);
      });

      return { ...s, organizational_units: newUnits, responsible_employees: newEmployees };
    }));

    // Get updated units and fetch employees
    const step = steps.find(s => s.id === stepId);
    if (step) {
      const currentUnits = step.organizational_units || [];
      const newUnits = currentUnits.includes(unitId)
        ? currentUnits.filter(id => id !== unitId)
        : [...currentUnits, unitId];
      fetchEmployeesForStep(stepId, newUnits);
    }
  };

  // Toggle employee selection (multi-select)
  const handleStepEmployeeToggle = (stepId: string, employeeId: number) => {
    setSteps(steps.map(s => {
      if (s.id !== stepId) return s;

      const currentEmployees = s.responsible_employees || [];
      let newEmployees: number[];

      if (currentEmployees.includes(employeeId)) {
        // Remove employee
        newEmployees = currentEmployees.filter(id => id !== employeeId);
      } else {
        // Add employee
        newEmployees = [...currentEmployees, employeeId];
      }

      return { ...s, responsible_employees: newEmployees };
    }));
  };

  // KPI handlers
  const handleAddKPI = () => {
    setKpis([...kpis, createEmptyKPI()]);
  };

  const handleRemoveKPI = (kpiId: string) => {
    const target = kpis.find(k => k.id === kpiId);
    const numericId = parseInt(kpiId, 10);
    const backendId = !isNaN(numericId) && numericId > 0 ? numericId : null;
    setKpiPendingDelete({
      id: kpiId,
      backendId,
      name: (target as any)?.name || '',
    });
  };

  const confirmRemoveKPI = async () => {
    if (!kpiPendingDelete) return;
    const { id, backendId } = kpiPendingDelete;
    if (backendId !== null) {
      try {
        await kpisApi.delete(backendId);
        showSuccess(t('kpis.deleteSuccess'));
      } catch (err) {
        showError(t('kpis.deleteFailed'));
        setKpiPendingDelete(null);
        return;
      }
    }
    setKpis(prev => prev.filter(k => k.id !== id));
    setKpiPendingDelete(null);
  };

  const handleKPIChange = (kpiId: string, field: keyof KPIFormData, value: string) => {
    setKpis(kpis.map(k => k.id === kpiId ? { ...k, [field]: value } : k));
  };

  const toggleKPIExpand = (kpiId: string) => {
    setKpis(kpis.map(k => k.id === kpiId ? { ...k, isExpanded: !k.isExpanded } : k));
  };

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={[styles.loadingText, { color: themeColors.loadingText }]}>{t('common.loadingData')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView ref={scrollViewRef} style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.container, isSmallScreen && styles.containerSmall]}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: t('navigation.home') },
              { label: t('navigation.processes'), route: 'Processes' },
              { label: isEditMode ? t('processes.editSubProcess') : t('processes.addSubProcess') },
            ]}
          />

          {/* Main Card Container */}
          <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder, overflow: 'visible', zIndex: 10 }]}>
          {/* Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('processes.editSubProcess') : t('processes.addSubProcess')}</Text>

          {/* Basic Information Section */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: (showGoalDropdown || showProcessDropdown || showUnitDropdown) ? 9999 : 30, overflow: 'visible' }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('common.basicInfo')}</Text>

            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: showGoalDropdown ? 9999 : 10, overflow: 'visible' }]}>
              {/* العملية الفرعية — first part of the combined name */}
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {t('operations.subProcess')} <Text style={styles.required}>*</Text>
                </Text>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.subName ? styles.inputError : undefined]}
                  value={formData.subName}
                  onChangeText={(text) => setFormData({ ...formData, subName: text })}
                  placeholder=""
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>

              {/* Strategic Goal - Disabled when inherited from parent */}
              <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: showGoalDropdown ? 9999 : 1, overflow: 'visible' }]}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {t('goals.strategicGoal')} <Text style={styles.required}>*</Text>
                  {isInheritedFromParent && <Text style={styles.inheritedLabel}> ({t('common.inherited')})</Text>}
                </Text>
                <TouchableOpacity
                  ref={goalDropdownRef}
                  data-dropdown-trigger="true"
                  style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: themeColors.dropdownBorder }, isInheritedFromParent && styles.dropdownDisabled]}
                  disabled={isInheritedFromParent}
                  onPress={() => {
                    if (!isInheritedFromParent) {
                      handleOpenGoalDropdown();
                    }
                  }}
                >
                  <ChevronDownIcon size={16} color={isInheritedFromParent ? themeColors.placeholder : themeColors.iconColor} />
                  <Text style={[styles.dropdownText, { color: themeColors.dropdownText }, formData.strategicGoal ? { color: themeColors.dropdownTextSelected } : undefined]}>
                    {formData.strategicGoal
                      ? translate(goals.find(g => g.id === formData.strategicGoal)?.title) || t('goals.selectStrategicGoal')
                      : t('goals.selectStrategicGoal')}
                  </Text>
                </TouchableOpacity>
                {!isInheritedFromParent && (
                  <PortalDropdown
                    isOpen={showGoalDropdown}
                    onClose={() => setShowGoalDropdown(false)}
                    buttonRef={goalDropdownRef}
                    backgroundColor={themeColors.dropdownMenu}
                    borderColor={themeColors.dropdownMenuBorder}
                  >
                    {goals.map(goal => (
                      <TouchableOpacity
                        key={goal.id}
                        style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                        onPress={() => {
                          setFormData({ ...formData, strategicGoal: goal.id });
                          setShowGoalDropdown(false);
                        }}
                      >
                        <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{translate(goal.title)}</Text>
                      </TouchableOpacity>
                    ))}
                  </PortalDropdown>
                )}
              </View>
            </View>

            {/* اسم العملية — second part; saved as "{العملية الفرعية} - {اسم العملية}" */}
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: themeColors.label }]}>
                {t('processes.processName')} <Text style={styles.required}>*</Text>
              </Text>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.opName ? styles.inputError : undefined]}
                value={formData.opName}
                onChangeText={(text) => setFormData({ ...formData, opName: text })}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
            </View>

            {/* Sub-process Code (رمز العملية الفرعية) — segment of the code path */}
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: themeColors.label }]}>{t('operations.operationCode')}</Text>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.code}
                onChangeText={(text) => setFormData({ ...formData, code: text })}
                placeholder={t('operations.operationCodeHint')}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
            </View>

            {/* Description - الهدف من العملية */}
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: themeColors.label }]}>
                {t('processes.processDescription')} <Text style={styles.required}>*</Text>
              </Text>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.description}
                onChangeText={(text) => setFormData({ ...formData, description: text })}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: (showProcessDropdown || showUnitDropdown) ? 9999 : 20, overflow: 'visible' }]}>
              {/* Parent Process - Disabled when inherited from parent */}
              <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: showProcessDropdown ? 9999 : 2, overflow: 'visible' }]}>
                <Text style={styles.label}>
                  {t('subProcesses.mainProcess')} <Text style={styles.required}>*</Text>
                  {isInheritedFromParent && <Text style={styles.inheritedLabel}> ({t('common.inherited')})</Text>}
                </Text>
                <TouchableOpacity
                  ref={processDropdownRef}
                  data-dropdown-trigger="true"
                  style={[styles.dropdown, isInheritedFromParent && styles.dropdownDisabled, errors.parentProcess ? styles.inputError : undefined]}
                  disabled={isInheritedFromParent}
                  onPress={() => {
                    if (!isInheritedFromParent) {
                      handleOpenProcessDropdown();
                    }
                  }}
                >
                  <ChevronDownIcon size={16} color={isInheritedFromParent ? "#9CA3AF" : "#6B7280"} />
                  <Text style={[styles.dropdownText, formData.parentProcess ? styles.dropdownTextSelected : undefined]}>
                    {formData.parentProcess
                      ? (isInheritedFromParent && parentProcessName ? translate(parentProcessName) : translate(processes.find(p => p.id === formData.parentProcess)?.name) || t('processes.selectMainProcess'))
                      : t('processes.selectMainProcess')}
                  </Text>
                </TouchableOpacity>
                {!isInheritedFromParent && (
                  <PortalDropdown
                    isOpen={showProcessDropdown}
                    onClose={() => setShowProcessDropdown(false)}
                    buttonRef={processDropdownRef}
                    backgroundColor={themeColors.dropdownMenu}
                    borderColor={themeColors.dropdownMenuBorder}
                  >
                    {processes.map(process => (
                      <TouchableOpacity
                        key={process.id}
                        style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                        onPress={() => {
                          setFormData({ ...formData, parentProcess: process.id });
                          setShowProcessDropdown(false);
                        }}
                      >
                        <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{translate(process.name)}</Text>
                      </TouchableOpacity>
                    ))}
                  </PortalDropdown>
                )}
              </View>

              {/* Organizational Unit - Disabled when inherited from parent */}
              <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: showUnitDropdown ? 9999 : 1, overflow: 'visible' }]}>
                <Text style={styles.label}>
                  {t('unitGoals.organizationalUnit')} <Text style={styles.required}>*</Text>
                  {isInheritedFromParent && <Text style={styles.inheritedLabel}> ({t('common.inherited')})</Text>}
                </Text>
                <TouchableOpacity
                  ref={unitDropdownRef}
                  data-dropdown-trigger="true"
                  style={[styles.dropdown, isInheritedFromParent && styles.dropdownDisabled]}
                  disabled={isInheritedFromParent}
                  onPress={() => {
                    if (!isInheritedFromParent) {
                      handleOpenUnitDropdown();
                    }
                  }}
                >
                  <ChevronDownIcon size={16} color={isInheritedFromParent ? "#9CA3AF" : "#6B7280"} />
                  <Text style={[styles.dropdownText, formData.organizationalUnit ? styles.dropdownTextSelected : undefined]}>
                    {formData.organizationalUnit
                      ? translateUnit(units.find(u => u.id === formData.organizationalUnit)?.name_ar || units.find(u => u.id === formData.organizationalUnit)?.name) || t('common.selectResponsible')
                      : t('common.selectResponsible')}
                  </Text>
                </TouchableOpacity>
                {!isInheritedFromParent && (
                  <PortalDropdown
                    isOpen={showUnitDropdown}
                    onClose={() => setShowUnitDropdown(false)}
                    buttonRef={unitDropdownRef}
                    backgroundColor={themeColors.dropdownMenu}
                    borderColor={themeColors.dropdownMenuBorder}
                  >
                    {units.map(unit => (
                      <TouchableOpacity
                        key={unit.id}
                        style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                        onPress={() => {
                          setFormData({ ...formData, organizationalUnit: unit.id });
                          setShowUnitDropdown(false);
                        }}
                      >
                        <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{translateUnit(unit.name_ar || unit.name)}</Text>
                      </TouchableOpacity>
                    ))}
                  </PortalDropdown>
                )}
              </View>
            </View>

            {/* Row 4: زمن العملية | وحدة الزمن */}
            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: showTimeUnitDropdown ? 9999 : 5, overflow: 'visible' }]}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('subProcesses.processTime')}</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={formData.processTime}
                  onChangeText={(text) => {
                    const intValue = normalizeArabicNumbers(text).replace(/[^0-9]/g, '');
                    setFormData({ ...formData, processTime: intValue });
                  }}
                  placeholder="1"
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                  keyboardType="numeric"
                />
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: showTimeUnitDropdown ? 9999 : 1, overflow: 'visible' }]}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('subProcesses.timeUnitLabel')}</Text>
                <TouchableOpacity
                  ref={timeUnitDropdownRef}
                  data-dropdown-trigger="true"
                  style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: themeColors.dropdownBorder }]}
                  onPress={() => {
                    handleOpenTimeUnitDropdown();
                  }}
                >
                  <ChevronDownIcon size={16} color={themeColors.iconColor} />
                  <Text style={[styles.dropdownText, { color: themeColors.dropdownText }, formData.timeUnit ? { color: themeColors.dropdownTextSelected } : undefined]}>
                    {formData.timeUnit
                      ? timeUnitOptions.find(o => o.value === formData.timeUnit)?.label || t('subProcesses.selectTimeUnit')
                      : t('subProcesses.selectTimeUnit')}
                  </Text>
                </TouchableOpacity>
                {showTimeUnitDropdown && (
                  <View
                    data-dropdown-menu="true"
                    style={[styles.dropdownMenu, { backgroundColor: themeColors.dropdownMenu, borderColor: themeColors.dropdownMenuBorder }]}
                  >
                    <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                      {timeUnitOptions.map(option => (
                        <TouchableOpacity
                          key={option.value}
                          style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                          onPress={() => {
                            setFormData({ ...formData, timeUnit: option.value });
                            setShowTimeUnitDropdown(false);
                          }}
                        >
                          <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{option.label}</Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </View>
                )}
              </View>
            </View>

          </View>

          {/* Process Details Section - تفاصيل العملية */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: 15, overflow: 'visible' }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('processes.processDetails')}</Text>

            {/* Row 1: دورية التنفيذ | عدد الموظفين المنفذين للعملية */}
            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: showFrequencyDropdown ? 9999 : 5, overflow: 'visible' }]}>
              <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: showFrequencyDropdown ? 9999 : 1, overflow: 'visible', position: 'relative' as const }]}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('processes.executionFrequency')}</Text>
                <TouchableOpacity
                  ref={frequencyDropdownRef}
                  data-dropdown-trigger="true"
                  style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: themeColors.dropdownBorder }]}
                  onPress={() => {
                    setShowFrequencyDropdown(!showFrequencyDropdown);
                    setShowTimeUnitDropdown(false);
                  }}
                >
                  <ChevronDownIcon size={16} color={themeColors.iconColor} />
                  <Text style={[styles.dropdownText, { color: themeColors.dropdownText }, formData.executionFrequency ? { color: themeColors.dropdownTextSelected } : undefined]}>
                    {formData.executionFrequency
                      ? executionFrequencyOptions.find(o => o.value === formData.executionFrequency)?.label || formData.executionFrequency
                      : t('subProcesses.selectExecutionFrequency')}
                  </Text>
                </TouchableOpacity>
                {showFrequencyDropdown && (
                  <View
                    data-dropdown-menu="true"
                    style={[styles.dropdownMenu, { backgroundColor: themeColors.dropdownMenu, borderColor: themeColors.dropdownMenuBorder, maxHeight: 250, overflow: 'auto' as any }]}
                  >
                    {executionFrequencyOptions.map(option => (
                      <TouchableOpacity
                        key={option.value}
                        style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                        onPress={() => {
                          setFormData({ ...formData, executionFrequency: option.value });
                          setShowFrequencyDropdown(false);
                        }}
                      >
                        <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{option.label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('subProcesses.executionEmployeesCount')}</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={formData.employeesCount}
                  onChangeText={(text) => setFormData({ ...formData, employeesCount: normalizeArabicNumbers(text) })}
                  placeholder="1"
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                  keyboardType="numeric"
                />
              </View>
            </View>

            {/* Row 2: تكلفة العملية | الاعتماد */}
            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('subProcesses.processCost')}</Text>
                <View style={[styles.inputWithSuffixContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, borderWidth: 1, borderRadius: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1 }]}>
                  <Text style={[styles.inputSuffixLeft, { color: themeColors.placeholder }]}>AED</Text>
                  <TextInput
                    style={[styles.inputFlex, { textAlign: 'right', color: themeColors.inputText }]}
                    value={formData.processCost}
                    onChangeText={(text) => setFormData({ ...formData, processCost: text })}
                    placeholder="1.0"
                    placeholderTextColor={themeColors.placeholder}
                    keyboardType="numeric"
                  />
                </View>
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('subProcesses.approvalPerson')}</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={formData.approvalPerson}
                  onChangeText={(text) => setFormData({ ...formData, approvalPerson: text })}
                  placeholder={t('subProcesses.approvalPersonPlaceholder')}
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>
            </View>

            {/* Row 3: صورة المسار التدفقي للعملية (full width) */}
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: themeColors.label }]}>{t('subProcesses.processFlowImages')}</Text>
              <TouchableOpacity style={[styles.fileInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]} onPress={handleFilePick}>
                <View style={styles.fileInputContent}>
                  <Text style={[styles.fileInputText, { color: themeColors.inputText }]}>
                    {selectedFile?.name || (existingFileUrl ? existingFileUrl.split('/').pop() : 'Filename.pdf')}
                  </Text>
                  <View style={styles.fileInputButton}>
                    <Text style={styles.fileInputButtonText}>{t('common.chooseFile')}</Text>
                  </View>
                </View>
              </TouchableOpacity>
              <View style={styles.fileHelpText}>
                <Text style={[styles.fileHelpTextContent, { color: themeColors.label }]}>
                  {t('subProcesses.flowImagesHelp')}
                </Text>
                <InfoIcon size={20} color={themeColors.label} />
              </View>
            </View>
          </View>

          {/* Flowchart Designer — interactive companion to the legacy
              file upload above. Sub-processes can use either or both. */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>
              {t('flowchart.sectionTitle')}
            </Text>
            {Platform.OS === 'web' ? (
              <FlowchartDesigner
                value={formData.flowchartData}
                // Canvas edits (add/remove/relabel/connect nodes) flow back to
                // the إجراءات العملية table via the bidirectional sync effect.
                onChange={(next) => {
                  syncSourceRef.current = 'flow';
                  setFormData((prev) => ({ ...prev, flowchartData: next }));
                }}
                height={460}
                toolbarLabels={{
                  start:           t('flowchart.nodeTypes.start'),
                  step:            t('flowchart.nodeTypes.step'),
                  decision:        t('flowchart.nodeTypes.decision'),
                  end:             t('flowchart.nodeTypes.end'),
                  addNode:         t('flowchart.toolbar.add'),
                  deleteSelected:  t('flowchart.toolbar.delete'),
                  editLabel:       t('flowchart.editLabel'),
                  saveLabel:       t('common.save'),
                  cancel:          t('common.cancel'),
                  placeholderLabel: t('flowchart.placeholderLabel'),
                  instruction:     t('flowchart.instruction'),
                  autoGenerate:             t('flowchart.autoGenerate.button'),
                  autoGenerateConfirmTitle: t('flowchart.autoGenerate.confirmTitle'),
                  autoGenerateConfirmBody:  t('flowchart.autoGenerate.confirmBody'),
                  autoGenerateEmptySteps:   t('flowchart.autoGenerate.emptySteps'),
                  autoGenerateProceed:      t('flowchart.autoGenerate.proceed'),
                }}
              />
            ) : (
              <View>
                <Text style={{ fontSize: 12, textAlign: 'right', marginBottom: 8, color: '#64748B' }}>
                  {t('flowchart.webOnly')}
                </Text>
                <FlowchartViewer
                  value={formData.flowchartData}
                  height={300}
                  emptyMessage={t('flowchart.emptyMessage')}
                />
              </View>
            )}
          </View>

          {/* Process Steps Section */}
          <View style={[styles.section, { zIndex: 10, borderRadius: 4, padding: 12, borderWidth: 1, borderColor: '#E2E8F0', overflow: 'visible' as const }]}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>{t('subProcesses.processSteps')}</Text>
            </View>

            {steps.map((step, index) => (
              <View key={step.id} style={styles.stepCardContainer}>
                <View style={[styles.stepCard, { backgroundColor: themeColors.stepCard, borderColor: themeColors.stepCardBorder }]}>
                  {/* Step Header */}
                  <View style={styles.stepHeader}>
                    {/* Left Side - Action Buttons */}
                    <View style={styles.stepHeaderLeft}>
                      <TouchableOpacity
                        style={styles.stepIconButton}
                        onPress={() => toggleStepExpand(step.id)}
                      >
                        {step.isExpanded ? (
                          <ChevronUpIcon size={16} color="#414042" />
                        ) : (
                          <ChevronDownIcon size={16} color="#414042" />
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.stepIconButton}
                        onPress={() => handleRemoveStep(step.id)}
                      >
                        <TrashIcon size={16} color="#414042" />
                      </TouchableOpacity>
                    </View>
                    {/* Right Side - Step Number + Name (when collapsed) */}
                    <View style={styles.stepHeaderRight}>
                      {!step.isExpanded && step.name ? (
                        <Text style={styles.stepCollapsedName} numberOfLines={1}>{step.name}</Text>
                      ) : null}
                      <View style={styles.stepNumber}>
                        <Text style={styles.stepNumberText}>{index + 1}</Text>
                      </View>
                    </View>
                  </View>

                  {step.isExpanded && (
                    <View style={styles.stepContent}>
                      {/* Row 1: الإجراء (Procedure) - Full width textarea */}
                      <View style={{ marginBottom: 0 }}>
                        <Text style={styles.label}>{t('subProcesses.stepProcedure')}</Text>
                        <TextInput
                          style={[styles.input, styles.textArea]}
                          value={step.name}
                          onChangeText={(text) => handleStepChange(step.id, 'name', text)}
                          placeholder=""
                          placeholderTextColor="#64748B"
                          textAlign="right"
                          multiline
                          numberOfLines={2}
                        />
                      </View>

                      {/* Procedure type (خطوة / قرار) — drives the flowchart node shape */}
                      <View style={{ marginBottom: 0, marginTop: 12 }}>
                        <Text style={styles.label}>{t('flowchart.stepType')}</Text>
                        <View style={{ flexDirection: 'row-reverse', gap: 8 }}>
                          {(['step', 'decision'] as const).map((opt) => {
                            const selected = (step.type || 'step') === opt;
                            return (
                              <TouchableOpacity
                                key={opt}
                                onPress={() => handleStepChange(step.id, 'type', opt)}
                                style={{
                                  flex: 1,
                                  paddingVertical: 10,
                                  borderRadius: 8,
                                  borderWidth: 1,
                                  borderColor: selected ? '#A9211F' : '#E2E8F0',
                                  backgroundColor: selected ? '#A9211F' : 'transparent',
                                  alignItems: 'center',
                                }}
                              >
                                <Text style={{ color: selected ? '#FFFFFF' : '#64748B', fontWeight: '600', fontSize: 13 }}>
                                  {opt === 'step' ? t('flowchart.nodeTypes.step') : t('flowchart.nodeTypes.decision')}
                                </Text>
                              </TouchableOpacity>
                            );
                          })}
                        </View>
                      </View>

                      {/* Row 2: المدة (right) + وحدة الزمن (left) - RTL row-reverse: first child = right */}
                      <View style={[styles.formRow, { marginBottom: 0, zIndex: 100 }, isSmallScreen && styles.formRowSmall]}>
                        <View style={[styles.formGroupHalf, { marginBottom: 0 }]}>
                          <Text style={styles.label}>{t('processes.duration')}</Text>
                          <TextInput
                            style={styles.input}
                            value={step.duration}
                            onChangeText={(text) => handleStepChange(step.id, 'duration', text)}
                            placeholder="1"
                            placeholderTextColor="#64748B"
                            textAlign="right"
                            keyboardType="numeric"
                          />
                        </View>
                        <View style={[styles.formGroupHalf, { zIndex: 1002, marginBottom: 0, position: 'relative' as const }]}>
                          <Text style={styles.label}>{t('processes.timeUnit')}</Text>
                          <TouchableOpacity
                            style={styles.dropdown}
                            onPress={() => {
                              setActiveStepTimeUnitDropdown(activeStepTimeUnitDropdown === step.id ? null : step.id);
                              setActiveStepUnitDropdown(null);
                              setActiveStepEmployeeDropdown(null);
                            }}
                          >
                            <View style={styles.dropdownContent}>
                              <ChevronDownIcon size={16} color="#414042" />
                              <Text style={[
                                styles.dropdownText,
                                step.timeUnit ? styles.dropdownTextSelected : null,
                              ]}>
                                {step.timeUnit
                                  ? timeUnitOptions.find(o => o.value === step.timeUnit)?.label || t('subProcesses.selectTimeUnit')
                                  : t('subProcesses.selectTimeUnit')}
                              </Text>
                            </View>
                          </TouchableOpacity>
                          {activeStepTimeUnitDropdown === step.id && (
                            <View style={[styles.dropdownMenu, { zIndex: 9999 }]}>
                              <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                {timeUnitOptions.map(option => (
                                  <TouchableOpacity
                                    key={option.value}
                                    style={styles.dropdownItem}
                                    onPress={() => {
                                      handleStepChange(step.id, 'timeUnit', option.value);
                                      setActiveStepTimeUnitDropdown(null);
                                    }}
                                  >
                                    <Text style={[
                                      styles.dropdownItemText,
                                      step.timeUnit === option.value && { color: '#A9211F', fontFamily: FONTS.bold }
                                    ]}>
                                      {option.label}
                                    </Text>
                                  </TouchableOpacity>
                                ))}
                              </ScrollView>
                            </View>
                          )}
                        </View>
                      </View>

                      {/* Row 3: المسؤول (right half) - RTL row-reverse: first child = right */}
                      <View style={[styles.formRow, { marginBottom: 0, zIndex: 1 }, isSmallScreen && styles.formRowSmall]}>
                        <View style={[styles.formGroupHalf, { marginBottom: 0 }]}>
                          <Text style={styles.label}>{t('processes.responsible')}</Text>
                          <TextInput
                            style={styles.input}
                            value={step.responsible}
                            onChangeText={(text) => handleStepChange(step.id, 'responsible', text)}
                            placeholder={t('subProcesses.addResponsiblePlaceholder')}
                            placeholderTextColor="#64748B"
                            textAlign="right"
                          />
                        </View>
                        <View style={[styles.formGroupHalf, { marginBottom: 0 }]} />
                      </View>
                    </View>
                  )}
                </View>

                {/* Drag Handle - Right side in RTL */}
                <View style={styles.dragHandle}>
                  <MenuIcon size={16} color="#414042" />
                </View>
              </View>
            ))}

            <TouchableOpacity style={styles.addStepButton} onPress={handleAddStep}>
              <Text style={styles.addStepButtonText}>{t('subProcesses.addNewStep')}</Text>
              <PlusIcon size={13} color="#414042" />
            </TouchableOpacity>
          </View>

          {/* KPIs Section */}
          <View
            ref={kpiSectionRef}
            style={[styles.section, { zIndex: 5 }]}
            onLayout={(event) => {
              const { y } = event.nativeEvent.layout;
              setKpiSectionY(y);
            }}
          >
            <View style={styles.kpiSectionHeader}>
              <TouchableOpacity style={styles.sectionToggleButton}>
                {kpis.length > 0 ? (
                  <ChevronUpIcon size={16} color="#414042" />
                ) : (
                  <ChevronDownIcon size={16} color="#414042" />
                )}
              </TouchableOpacity>
              <Text style={styles.sectionTitleNoMargin}>{t('kpis.performanceIndicators')}</Text>
            </View>

            {kpis.map((kpi, index) => (
              <View key={kpi.id} style={styles.kpiCardContainer}>
                <View style={[styles.kpiCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
                  {/* KPI Header */}
                  <View style={styles.kpiHeader}>
                    {/* Left Side - Action Buttons */}
                    <View style={styles.kpiHeaderLeft}>
                      <TouchableOpacity
                        style={styles.stepIconButton}
                        onPress={() => toggleKPIExpand(kpi.id)}
                      >
                        {kpi.isExpanded ? (
                          <ChevronUpIcon size={16} color="#414042" />
                        ) : (
                          <ChevronDownIcon size={16} color="#414042" />
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.stepIconButton, styles.stepIconButtonDisabled]}
                        onPress={() => handleRemoveKPI(kpi.id)}
                      >
                        <TrashIcon size={16} color="#414042" />
                      </TouchableOpacity>
                    </View>
                    {/* Right Side - KPI Number and Name */}
                    <View style={styles.kpiHeaderRight}>
                      {!kpi.isExpanded && kpi.name && (
                        <Text style={styles.kpiCollapsedName}>{kpi.name}</Text>
                      )}
                      <View style={styles.kpiNumberBadge}>
                        <Text style={styles.kpiNumberBadgeText}>{index + 1}</Text>
                      </View>
                    </View>
                  </View>

                  {kpi.isExpanded && (
                    <View style={styles.kpiContent}>
                      {/* المعلومات الأساسية */}
                      <View style={styles.kpiSubsection}>
                        <Text style={styles.subsectionTitle}>{t('common.basicInfo')}</Text>

                        {/* Row 1: اسم المؤشر (left) | الوزن with button (right) - Figma layout */}
                        <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
                          {/* اسم المؤشر - left side (appears first in row-reverse) */}
                          <View style={[styles.formGroup, styles.formGroupHalf]}>
                            <Text style={styles.label}>
                              {t('kpis.kpiName')} <Text style={styles.required}>*</Text>
                            </Text>
                            <TextInput
                              style={styles.input}
                              value={kpi.name}
                              onChangeText={(text) => handleKPIChange(kpi.id, 'name', text)}
                              placeholder=""
                              placeholderTextColor="#9CA3AF"
                              textAlign="right"
                            />
                          </View>
                          {/* الوزن - right side with تعيين الوزن button (appears second in row-reverse) */}
                          <View style={[styles.formGroup, styles.formGroupHalf]}>
                            <Text style={styles.label}>
                              {t('common.weight')} <Text style={styles.required}>*</Text>
                            </Text>
                            <View style={styles.inputWithButtonLeft}>
                              <View style={styles.inputWithSuffixContainer}>
                                <Text style={styles.inputSuffixLeft}>%</Text>
                                <TextInput
                                  style={[styles.inputFlex, { textAlign: 'right' }]}
                                  value={kpi.weight}
                                  onChangeText={(text) => handleKPIChange(kpi.id, 'weight', normalizeArabicNumbers(text))}
                                  placeholder="30"
                                  placeholderTextColor="#414042"
                                  keyboardType="numeric"
                                />
                              </View>
                              <TouchableOpacity style={styles.assignWeightButton}>
                                <Text style={styles.assignWeightButtonText}>{t('kpis.setWeight')}</Text>
                              </TouchableOpacity>
                            </View>
                          </View>
                        </View>

                        {/* وصف المؤشر - Full width textarea */}
                        <View style={styles.formGroup}>
                          <Text style={styles.label}>
                            {t('kpis.kpiDescription')} <Text style={styles.required}>*</Text>
                          </Text>
                          <TextInput
                            style={[styles.input, styles.textAreaSmall]}
                            value={kpi.description}
                            onChangeText={(text) => handleKPIChange(kpi.id, 'description', text)}
                            placeholder=""
                            placeholderTextColor="#9CA3AF"
                            textAlign="right"
                            multiline
                            numberOfLines={2}
                          />
                        </View>
                      </View>

                      {/* التصنيف - Figma layout: تصنيف المؤشر (right) | النمطية (left) */}
                      <View style={[styles.kpiSubsection, { zIndex: 20, overflow: 'visible' }]}>
                        <Text style={styles.subsectionTitle}>{t('common.classification')}</Text>

                        <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: 100, overflow: 'visible' }]}>
                          {/* النمطية */}
                          <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'polarity' ? 9999 : 1, overflow: 'visible' }]}>
                            <Text style={styles.label}>
                              {t('kpis.polarity')} <Text style={styles.required}>*</Text>
                            </Text>
                            <TouchableOpacity
                              style={styles.dropdown}
                              onPress={() => toggleKpiDropdown(kpi.id, 'polarity')}
                            >
                              <ChevronDownIcon size={16} color="#414042" />
                              <Text style={[styles.dropdownText, kpi.polarity ? styles.dropdownTextSelected : undefined]}>
                                {kpi.polarity
                                  ? polarityOptions.find(p => p.value === kpi.polarity)?.label || t('kpis.selectPolarity')
                                  : t('kpis.selectPolarity')}
                              </Text>
                            </TouchableOpacity>
                            {activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'polarity' && (
                              <View style={styles.dropdownMenu}>
                                <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                  {polarityOptions.map(option => (
                                    <TouchableOpacity
                                      key={option.value}
                                      style={styles.dropdownItem}
                                      onPress={() => {
                                        handleKPIChange(kpi.id, 'polarity', option.value);
                                        setActiveKpiDropdown(null);
                                      }}
                                    >
                                      <Text style={styles.dropdownItemText}>{option.label}</Text>
                                    </TouchableOpacity>
                                  ))}
                                </ScrollView>
                              </View>
                            )}
                          </View>
                        </View>

                        {/* KPI Type Row */}
                        <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: 99, overflow: 'visible' }]}>
                          <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'kpiType' ? 9999 : 1, overflow: 'visible' }]}>
                            <Text style={styles.label}>
                              {t('kpis.kpiType')} <Text style={styles.required}>*</Text>
                            </Text>
                            <TouchableOpacity
                              style={styles.dropdown}
                              onPress={() => toggleKpiDropdown(kpi.id, 'kpiType')}
                            >
                              <ChevronDownIcon size={16} color="#414042" />
                              <Text style={[styles.dropdownText, kpi.kpiType ? styles.dropdownTextSelected : undefined]}>
                                {kpi.kpiType
                                  ? kpiTypeOptions.find(o => o.value === kpi.kpiType)?.label || t('kpis.selectKpiType')
                                  : t('kpis.selectKpiType')}
                              </Text>
                            </TouchableOpacity>
                            {activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'kpiType' && (
                              <View style={styles.dropdownMenu}>
                                <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                  {kpiTypeOptions.map(option => (
                                    <TouchableOpacity
                                      key={option.value}
                                      style={styles.dropdownItem}
                                      onPress={() => {
                                        handleKPIChange(kpi.id, 'kpiType', option.value);
                                        setActiveKpiDropdown(null);
                                      }}
                                    >
                                      <Text style={styles.dropdownItemText}>{option.label}</Text>
                                    </TouchableOpacity>
                                  ))}
                                </ScrollView>
                              </View>
                            )}
                          </View>
                        </View>
                      </View>

                      {/* القياس */}
                      <View style={[styles.kpiSubsection, { zIndex: 10, overflow: 'visible' }]}>
                        <Text style={styles.subsectionTitle}>{t('kpis.measurement')}</Text>

                        {/* Row 1: تاريخ بداية القياس (right only) - Figma layout */}
                        <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
                          {/* تاريخ بداية القياس - right side (first in row-reverse) */}
                          <View style={[styles.formGroup, styles.formGroupHalf]}>
                            <Text style={styles.label}>
                              {t('kpis.measurementStartDate')} <Text style={styles.required}>*</Text>
                            </Text>
                            {Platform.OS === 'web' ? (
                              <input
                                type="date"
                                value={kpi.measurementStartDate || ''}
                                onChange={(e) => handleKPIChange(kpi.id, 'measurementStartDate', e.target.value)}
                                style={{
                                  backgroundColor: '#FFFFFF',
                                  borderWidth: 1,
                                  borderColor: '#E2E8F0',
                                  borderStyle: 'solid',
                                  borderRadius: 8,
                                  paddingLeft: 12,
                                  paddingRight: 12,
                                  paddingTop: 7.5,
                                  paddingBottom: 7.5,
                                  minHeight: 36,
                                  fontSize: 14,
                                  color: '#414042',
                                  textAlign: 'right',
                                  direction: 'rtl',
                                  width: '100%',
                                  boxSizing: 'border-box',
                                }}
                              />
                            ) : (
                              <View style={styles.inputWithIconLeft}>
                                <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                                  <Path d="M8 2V5" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <Path d="M16 2V5" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <Path d="M3 9H21" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <Path d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </Svg>
                                <TextInput
                                  style={[styles.inputFlex, { textAlign: 'right' }]}
                                  value={kpi.measurementStartDate}
                                  onChangeText={(text) => handleKPIChange(kpi.id, 'measurementStartDate', text)}
                                  placeholder="YYYY-MM-DD"
                                  placeholderTextColor="#64748B"
                                />
                              </View>
                            )}
                          </View>
                          {/* Empty spacer on left (second in row-reverse) */}
                          <View style={[styles.formGroup, styles.formGroupHalf]} />
                        </View>

                        {/* Row 2: وحدة القياس (right) | دورية القياس (left) - Figma layout */}
                        <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: 100, overflow: 'visible' }]}>
                          {/* وحدة القياس - right side (first in row-reverse) */}
                          <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'unit' ? 9999 : 1, overflow: 'visible' }]}>
                            <Text style={styles.label}>
                              {t('kpis.unit')} <Text style={styles.required}>*</Text>
                            </Text>
                            <TouchableOpacity
                              style={styles.dropdown}
                              onPress={() => toggleKpiDropdown(kpi.id, 'unit')}
                            >
                              <ChevronDownIcon size={16} color="#414042" />
                              <Text style={[styles.dropdownText, kpi.unit ? styles.dropdownTextSelected : undefined]}>
                                {kpi.unit
                                  ? unitOptions.find(u => u.value === kpi.unit)?.label || t('kpis.selectUnit')
                                  : t('kpis.selectUnit')}
                              </Text>
                            </TouchableOpacity>
                            {activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'unit' && (
                              <View style={styles.dropdownMenu}>
                                <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                  {unitOptions.map(option => (
                                    <TouchableOpacity
                                      key={option.value}
                                      style={styles.dropdownItem}
                                      onPress={() => {
                                        handleKPIChange(kpi.id, 'unit', option.value);
                                        setActiveKpiDropdown(null);
                                      }}
                                    >
                                      <Text style={styles.dropdownItemText}>{option.label}</Text>
                                    </TouchableOpacity>
                                  ))}
                                </ScrollView>
                              </View>
                            )}
                          </View>
                          {/* دورية القياس - left side (second in row-reverse) */}
                          <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'frequency' ? 9999 : 1, overflow: 'visible' }]}>
                            <Text style={styles.label}>
                              {t('kpis.measurementFrequency')} <Text style={styles.required}>*</Text>
                            </Text>
                            <TouchableOpacity
                              style={styles.dropdown}
                              onPress={() => toggleKpiDropdown(kpi.id, 'frequency')}
                            >
                              <ChevronDownIcon size={16} color="#414042" />
                              <Text style={[styles.dropdownText, kpi.frequency ? styles.dropdownTextSelected : undefined]}>
                                {kpi.frequency
                                  ? frequencyOptions.find(f => f.value === kpi.frequency)?.label || t('kpis.selectFrequency')
                                  : t('kpis.selectFrequency')}
                              </Text>
                            </TouchableOpacity>
                            {activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'frequency' && (
                              <View style={styles.dropdownMenu}>
                                <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                  {frequencyOptions.map(option => (
                                    <TouchableOpacity
                                      key={option.value}
                                      style={styles.dropdownItem}
                                      onPress={() => {
                                        handleKPIChange(kpi.id, 'frequency', option.value);
                                        setActiveKpiDropdown(null);
                                      }}
                                    >
                                      <Text style={styles.dropdownItemText}>{option.label}</Text>
                                    </TouchableOpacity>
                                  ))}
                                </ScrollView>
                              </View>
                            )}
                          </View>
                        </View>

                        {/* معادلة القياس - Full width textarea */}
                        <View style={styles.formGroup}>
                          <Text style={styles.label}>
                            {t('kpis.measurementFormula')} <Text style={styles.required}>*</Text>
                          </Text>
                          <TextInput
                            style={[styles.input, styles.textAreaSmall]}
                            value={kpi.formula}
                            onChangeText={(text) => handleKPIChange(kpi.id, 'formula', text)}
                            placeholder=""
                            placeholderTextColor="#64748B"
                            textAlign="right"
                            multiline
                            numberOfLines={2}
                          />
                        </View>

                        {/* Row 4: القيمة المستهدفة (right) | مصدر البيانات (left) - Figma layout */}
                        <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
                          {/* القيمة المستهدفة - right side (first in row-reverse) */}
                          <View style={[styles.formGroup, styles.formGroupHalf]}>
                            <Text style={styles.label}>
                              {t('kpis.targetValue')} <Text style={styles.required}>*</Text>
                            </Text>
                            <TextInput
                              style={styles.input}
                              value={kpi.targetValue}
                              onChangeText={(text) => handleKPIChange(kpi.id, 'targetValue', normalizeArabicNumbers(text))}
                              placeholder="0.0"
                              placeholderTextColor="#64748B"
                              textAlign="right"
                              keyboardType="numeric"
                            />
                          </View>
                          {/* مصدر البيانات - left side (second in row-reverse) */}
                          <View style={[styles.formGroup, styles.formGroupHalf]}>
                            <Text style={styles.label}>
                              {t('kpis.dataSource')} <Text style={styles.required}>*</Text>
                            </Text>
                            <TextInput
                              style={styles.input}
                              value={kpi.dataSource}
                              onChangeText={(text) => handleKPIChange(kpi.id, 'dataSource', text)}
                              placeholder=""
                              placeholderTextColor="#64748B"
                              textAlign="right"
                            />
                          </View>
                        </View>

                        {/* Worst Acceptable Value — only for negative polarity + non-% unit */}
                        {kpi.polarity === 'negative' && kpi.unit && kpi.unit !== '%' && (
                          <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
                            <View style={[styles.formGroup, styles.formGroupHalf]}>
                              <Text style={styles.label}>
                                {t('kpis.worstAcceptableValue')}
                                {parseFloat(kpi.targetValue) === 0 && (
                                  <Text style={styles.required}> *</Text>
                                )}
                              </Text>
                              <TextInput
                                style={styles.input}
                                value={kpi.worstAcceptableValue}
                                onChangeText={(text) => handleKPIChange(kpi.id, 'worstAcceptableValue', normalizeArabicNumbers(text))}
                                placeholder={t('kpis.worstAcceptableValueHint')}
                                placeholderTextColor="#64748B"
                                textAlign="right"
                                keyboardType="numeric"
                              />
                            </View>
                          </View>
                        )}
                      </View>
                    </View>
                  )}
                </View>

                {/* Drag Handle */}
                <View style={styles.dragHandle}>
                  <MenuIcon size={16} color="#414042" />
                </View>
              </View>
            ))}

            <TouchableOpacity style={[styles.addKpiButton, { backgroundColor: themeColors.addButton, borderColor: themeColors.addButtonBorder }]} onPress={handleAddKPI}>
              <Text style={[styles.addKpiButtonText, { color: themeColors.addButtonText }]}>{t('kpis.addKPI')}</Text>
              <PlusIcon size={14} color={themeColors.iconColor} />
            </TouchableOpacity>
          </View>

          {/* Footer Buttons */}
          <View style={styles.footer}>
            <TouchableOpacity style={[styles.cancelButton, { backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }]} onPress={handleCancel}>
              <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.saveButton, submitting && styles.saveButtonDisabled]}
              onPress={handleSave}
              disabled={submitting}
            >
              {submitting ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditMode ? t('common.update') : t('common.save')}</Text>
              )}
            </TouchableOpacity>
          </View>
          </View>
        </View>
      </ScrollView>
      <KPIDeleteConfirmModal
        visible={kpiPendingDelete !== null}
        kpiId={kpiPendingDelete?.backendId ?? null}
        kpiName={kpiPendingDelete?.name}
        onConfirm={confirmRemoveKPI}
        onCancel={() => setKpiPendingDelete(null)}
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    padding: 24,
    paddingTop: 16,
  },
  containerSmall: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  breadcrumbItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  breadcrumbText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },

  // Main Card
  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },

  // Title
  pageTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 24,
  },

  // Section
  section: {
    backgroundColor: '#F1F5F9',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 16,
  },

  // Form
  formRow: {
    flexDirection: 'row-reverse',
    gap: 16,
    marginBottom: 16,
  },
  formRowSmall: {
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: 16,
  },
  formGroupHalf: {
    flex: 1,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 8,
  },
  required: {
    color: '#EF4444',
  },
  inheritedLabel: {
    color: '#64748B',
    fontSize: 12,
    fontFamily: FONTS.regular,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  inputError: {
    borderColor: '#EF4444',
  },
  textArea: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  // Input with button (for اختيار مسؤول)
  inputWithButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    overflow: 'hidden',
  },
  inputWithButtonField: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
    borderWidth: 0,
  },
  selectResponsibleButton: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderLeftWidth: 1,
    borderLeftColor: '#E5E7EB',
  },
  selectResponsibleButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#0F172A',
  },

  // Dropdown
  dropdown: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    gap: 8,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dropdownDisabled: {
    opacity: 0.5,
  },
  dropdownText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  dropdownTextSelected: {
    color: '#414042',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    left: 0,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    marginTop: 4,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 10,
    zIndex: 9999,
    maxHeight: 200,
    overflow: 'hidden',
  },
  dropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  dropdownContent: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    flex: 1,
    gap: 8,
  },
  dropdownPlaceholder: {
    color: '#9CA3AF',
  },
  inlineDropdownOptions: {
    marginTop: 8,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    maxHeight: 150,
    overflow: 'hidden',
  },
  inlineDropdownOption: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  inlineDropdownOptionSelected: {
    backgroundColor: '#FEF2F2',
  },
  inlineDropdownOptionText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  inlineDropdownOptionTextSelected: {
    color: '#A9211F',
    fontFamily: FONTS.bold,
  },
  dropdownGroupLabel: {
    fontSize: 12,
    fontFamily: FONTS.bold,
    color: '#6B7280',
    textAlign: 'right',
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 4,
    backgroundColor: '#F3F4F6',
  },
  noDataText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    textAlign: 'center',
    paddingVertical: 16,
  },

  // Searchable Multi-Select Dropdown Styles
  searchableDropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    marginTop: 4,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 10,
    zIndex: 9999,
    maxHeight: 280,
  },
  dropdownSearchContainer: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  dropdownSearchInput: {
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownOptionsList: {
    maxHeight: 200,
  },
  dropdownOptionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    gap: 10,
  },
  dropdownOptionItemSelected: {
    backgroundColor: '#FEF2F2',
  },
  optionCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionCheckboxSelected: {
    backgroundColor: '#A9211F',
    borderColor: '#A9211F',
  },
  optionCheckmark: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: 'bold',
  },
  dropdownOptionText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownOptionTextSelected: {
    color: '#A9211F',
    fontFamily: FONTS.bold,
  },
  noOptionsText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    textAlign: 'center',
    paddingVertical: 20,
  },
  employeeOptionInfo: {
    flex: 1,
    alignItems: 'flex-end',
  },
  employeeOptionPosition: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
    marginTop: 2,
  },
  selectedTagsContainer: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
  },
  selectedTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF2F2',
    borderRadius: 16,
    paddingVertical: 4,
    paddingLeft: 8,
    paddingRight: 12,
    gap: 6,
  },
  selectedTagText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#A9211F',
  },
  removeTagButton: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#A9211F',
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeTagText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
    lineHeight: 16,
  },

  // Step Card Container (with drag handle)
  stepCardContainer: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 24,
    overflow: 'visible' as const,
    position: 'relative' as const,
    zIndex: 2,
  },
  dragHandle: {
    paddingTop: 24,
  },
  // Step Card
  stepCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    overflow: 'visible',
    padding: 12,
    gap: 24,
  },
  stepHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stepHeaderRight: {
    flex: 1,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 12,
  },
  stepHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  stepIconButton: {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    borderRadius: 8,
    backgroundColor: 'rgba(51, 65, 85, 0.05)',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepIconButtonDisabled: {
    opacity: 0.5,
  },
  stepNumber: {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    borderRadius: 4,
    backgroundColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  stepNumberText: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    fontWeight: '700',
    color: '#414042',
    lineHeight: 26,
  },
  stepTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  stepCollapsedName: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  stepContent: {
    gap: 24,
    overflow: 'visible' as const,
  },
  removeButton: {
    padding: 4,
  },
  // Input with icon
  inputWithIcon: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    gap: 8,
  },
  inputFlex: {
    flex: 1,
    borderWidth: 0,
    padding: 0,
    backgroundColor: 'transparent',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  // Input with suffix
  inputWithSuffix: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    gap: 8,
  },
  inputSuffix: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  // Input with button on right side (for تعيين الوزن)
  inputWithButtonRight: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 10,
  },
  inputWithButtonLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  inputWithSuffixContainer: {
    flex: 1,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    gap: 8,
  },
  inputDisabled: {
    opacity: 0.5,
  },
  inputSuffixLeft: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  assignWeightButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    minHeight: 36,
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  assignWeightButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  // Field hint (for دورية التنفيذ)
  fieldHint: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginTop: 8,
  },
  fieldHintText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // File input
  fileInput: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
  },
  fileInputContent: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  fileInputText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  fileInputButton: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  fileInputButtonText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#0F172A',
  },
  fileHelpText: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginTop: 8,
  },
  fileHelpTextContent: {
    flex: 1,
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Form group third
  formGroupThird: {
    flex: 1,
  },
  textAreaSmall: {
    minHeight: 60,
    textAlignVertical: 'top',
  },

  // KPI Section Header
  kpiSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  sectionToggleButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: 'rgba(51, 65, 85, 0.05)',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTitleNoMargin: {
    flex: 1,
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
  },
  // KPI Card Container (with drag handle)
  kpiCardContainer: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  // KPI Card
  kpiCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    overflow: 'hidden',
  },
  kpiHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
  },
  kpiHeaderRight: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 12,
  },
  kpiHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  kpiNumberBadge: {
    width: 40,
    height: 40,
    borderRadius: 4,
    backgroundColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpiNumberBadgeText: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  kpiCollapsedName: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  kpiNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: COLORS.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpiNumberText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  kpiTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  kpiContent: {
    padding: 12,
    paddingTop: 0,
    backgroundColor: '#FFFFFF',
    gap: 40,
    overflow: 'visible',
  },
  kpiSubsection: {
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 4,
    padding: 24,
    overflow: 'visible',
    zIndex: 1,
  },
  subsectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    fontWeight: '700',
    color: '#414042',
    textAlign: 'right',
    marginBottom: 24,
  },

  // Add Button (for steps)
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingVertical: 12,
    gap: 8,
  },
  addButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },

  // Add Step Button (styled per Figma)
  addStepButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    minHeight: 36,
    gap: 8,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addStepButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },

  // Add KPI Button (styled per Figma)
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    minHeight: 36,
    gap: 8,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addKpiButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },

  // Input with icon on left side (for date picker, unit selector)
  inputWithIconLeft: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    gap: 8,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },

  // Footer
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 12,
    marginTop: 8,
    marginBottom: 24,
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  saveButton: {
    backgroundColor: COLORS.primary,
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
});
